import { CHUNK_SIZE, type ChunkCoord } from '../core/world';
import { hashCombine, hashString } from './noise';

/**
 * Border handshake (ADR-0006).
 *
 * Each chunk shares an edge with each of its six hex-neighbour chunks. For
 * the four "thick" edges (E/W toward (±1, 0); NW/SE toward (0, ∓1)), the
 * passable position is one of CHUNK_SIZE positions along the border,
 * chosen by hashing the canonical edge key under the world seed. Both
 * neighbour chunks compute the same canonical key and so pick the same
 * position — guaranteeing a matched passable pair across the edge.
 *
 * The two diagonal edges (NE toward (+1, -1); SW toward (-1, +1)) share a
 * single corner-tile pair. We make those corner tiles unconditionally
 * passable on both sides — the handshake there is degenerate but still
 * canonical: both chunks agree without consulting a hash.
 */

/**
 * Canonical key for the edge between two chunks. Lex-ordered so both
 * neighbours derive the same string regardless of which one asks.
 */
function canonicalEdgeKey(a: ChunkCoord, b: ChunkCoord): string {
  const aFirst = a.cq < b.cq || (a.cq === b.cq && a.cr < b.cr);
  const lo = aFirst ? a : b;
  const hi = aFirst ? b : a;
  return `${lo.cq},${lo.cr}|${hi.cq},${hi.cr}`;
}

/** Hash a shared edge under a world seed. Symmetric in (a, b). */
export function edgeHash(a: ChunkCoord, b: ChunkCoord, seed: number): number {
  return hashCombine(seed >>> 0, hashString(canonicalEdgeKey(a, b)));
}

/** Position index in [0, CHUNK_SIZE) for the passable cell on a thick edge. */
export function edgePosition(a: ChunkCoord, b: ChunkCoord, seed: number): number {
  return edgeHash(a, b, seed) % CHUNK_SIZE;
}

/**
 * Forced-passable border cells (in local chunk coords) that the connectivity
 * step must unify into one component. Returned in deterministic order.
 *
 * Layout (8×8):
 *   - local q (lq) ∈ [0, 7] maps to world q = cq*8 + lq
 *   - local r (lr) ∈ [0, 7] maps to world r = cr*8 + lr
 *   - "east column" = lq = 7  (toward (+1, 0))
 *   - "west column" = lq = 0  (toward (-1, 0))
 *   - "north row"   = lr = 0  (toward (0, -1))
 *   - "south row"   = lr = 7  (toward (0, +1))
 *   - NE corner cell is (lq=7, lr=0)  (toward (+1, -1))
 *   - SW corner cell is (lq=0, lr=7)  (toward (-1, +1))
 */
export function forcedBorderCells(
  coord: ChunkCoord,
  seed: number,
): ReadonlyArray<readonly [number, number]> {
  const { cq, cr } = coord;
  const last = CHUNK_SIZE - 1;
  const posE = edgePosition(coord, { cq: cq + 1, cr }, seed);
  const posW = edgePosition({ cq: cq - 1, cr }, coord, seed);
  const posNW = edgePosition({ cq, cr: cr - 1 }, coord, seed);
  const posSE = edgePosition(coord, { cq, cr: cr + 1 }, seed);
  return [
    [last, posE], // east-edge passable cell
    [0, posW], // west-edge passable cell
    [posNW, 0], // north-edge passable cell
    [posSE, last], // south-edge passable cell
    [last, 0], // NE corner
    [0, last], // SW corner
  ];
}
